import * as React from 'react';

import { ActivityIndicator, Keyboard, ListRenderItemInfo } from 'react-native';

import { Colors } from 'theme/colors';
import { FontFamily, FontWeight } from 'theme/fonts';
import { InlineError } from 'theme/errors';
import { List, ListContainer, ListItem, ListItemText } from 'theme/list';
import {
  SearchContainer,
  SearchInput,
  SearchInputContainer,
  SearchInputLoader,
} from 'theme/search';

const ACTIVE_OPACITY = 0.7;
const ITEMS_PER_PAGE = 30;

const InlineErrorStyle = {
  marginLeft: 5,
  marginRight: 5,
  marginTop: 5,
  paddingVertical: 5,
};

/**
 * Search list component for displaying complex labelled items.
 *
 */
export class ListSearch<T extends MatchOption> extends React.PureComponent<Props<T>> {
  private cursor = 0;

  public constructor(props: Props<T>) {
    super(props);
    this.renderItem = this.renderItem.bind(this);
  }

  public render() {
    const { bottom, loading, message, options, placeholder, search } = this.props;

    return (
      <SearchContainer bottom={bottom}>
        {message && <InlineError style={InlineErrorStyle} error={message} />}
        {options.length > 0 && (
          <ListContainer>
            <List<Option<T>>
              data={options}
              initialNumToRender={ITEMS_PER_PAGE}
              keyboardShouldPersistTaps="always"
              keyExtractor={extractKey}
              renderItem={this.renderItem}
              showsVerticalScrollIndicator
            />
          </ListContainer>
        )}
        <SearchInputContainer>
          <SearchInput
            onChangeText={search}
            autoFocus={false}
            placeholder={placeholder}
            placeholderTextColor={Colors.darkGrey}
            onSubmitEditing={Keyboard.dismiss}
            returnKeyType="done"
            autoCorrect={false}
          />
          {loading && (
            <SearchInputLoader>
              <ActivityIndicator size="small" />
            </SearchInputLoader>
          )}
        </SearchInputContainer>
      </SearchContainer>
    );
  }

  private onSelect(item: T) {
    const { onSelect } = this.props;

    return () => onSelect(item);
  }

  private renderItem(payload: ListRenderItemInfo<Option<T>>) {
    const { loading } = this.props;

    const labels = payload.item.item.label
      ? payload.item.item.label.replace(', South Africa', '').split(',').slice(0, 2)
      : ['', ''];

    return (
      <ListItem
        activeOpacity={ACTIVE_OPACITY}
        onPress={this.onSelect(payload.item.item)}
        disabled={loading}>
        {this.renderText(labels[0], payload.item.item.matched_substrings)}
        {this.cursor < labels[0].length && (
          <ListItemText>{labels[0].substring(this.cursor)}</ListItemText>
        )}
        <ListItemText color={Colors.darkGrey}>{labels[1]}</ListItemText>
      </ListItem>
    );
  }

  private renderText(label: string, matches?: TextMatch[]) {
    this.cursor = 0;

    if (!matches) {
      return [];
    }

    return matches.map(match => {
      const { length, offset } = match;
      const prevText = label.substring(this.cursor, offset);
      const boldText = label.substr(offset, length);

      this.cursor = offset + length;

      return [
        <ListItemText key={`address-${offset}${length}`}>{prevText}</ListItemText>,
        <ListItemText
          key={`address-bold-${offset}${length}`}
          fontFamily={FontFamily.Bold}
          fontWeight={FontWeight.Bold}>
          {boldText}
        </ListItemText>,
      ];
    });
  }
}

// ! TODO: Fix the use of list search
/**
 * Generates a random key to ensure re-render.
 * Note: This should be replaced by not using a FlatList or any of its family.
 */
function extractKey() {
  const start = 2;
  const end = 10;
  const base = 36;

  return Math.random().toString(base).substr(start, end);
}
interface TextMatch {
  length: number;
  offset: number;
}

interface MatchOption {
  label: string;
  matched_substrings?: TextMatch[];
  value: string;
}
interface FuseMatch {
  arrayIndex: number;
  indices: number[][];
  key: string;
  value: string;
}
interface Option<T extends MatchOption> {
  item: T;
  matches?: FuseMatch[];
}
interface Props<T extends MatchOption> {
  bottom: number;
  delay?: number;
  error: boolean;
  loading: boolean;
  message?: string;
  options: Option<T>[];
  placeholder?: string;
  onSelect(option: T): void;
  search(searchText: string): void;
}
